import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { usePayment } from 'contexts/payment'
import { useSmartEntity } from 'contexts/smart-entity'
import { size } from 'utilities/object'
import Form from 'components/form/controller'
import EntityField from 'components/form/field/entity'
import Sources from './sources'
import LanguageField from 'components/form/field/language'
import CountryField from 'components/form/field/country-picker'
import { QuickSelectDumb } from '../quick-select'
import ContentLanguageSupportMessage from 'pages/documents/components/content-language-support-message'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'

const AddSmartDocumentContent = ({ form, setForm, next, initialTemplate, templatePickerRef, resolveTemplate, resolving, emptyOption, modal, salt }) => {
    const { formatMessage } = useIntl()
    const { locale } = useI18n()
    const { account } = usePayment()
    const { entity } = useSmartEntity()

    return (
        <Form
            layout="vertical"
            onSubmit={next}>
            {({ errors, trigger }) => (
                <>
                    <EntityField
                        salt={salt}
                        label={formatMessage({
                            id: 'documents_smart_template_noun',
                            defaultMessage: 'Smart template'
                        })}
                        name="template"
                        field={{
                            value: !!entity?.id ?
                                entity
                                : (form?.template ?? emptyOption),
                            include: 'never',
                            unsettable: !!entity?.id
                        }}
                        picker={{
                            heading: formatMessage({
                                id: 'documents_smart_templates_action_pick',
                                defaultMessage: 'Pick smart template'
                            }),
                            search: {
                                placeholder: formatMessage({
                                    id: 'documents_smart_templates_action_find',
                                    defaultMessage: 'Find smart template'
                                })
                            },
                            emptyOption,
                            outer: false
                        }}
                        entity={{
                            type: 'smartTemplate',
                            path: '/documents/templates',
                            showIcon: false
                        }}
                        onChange={({ template }) => resolveTemplate(template)}
                        enabled={!initialTemplate && !resolving}
                        whistle={entity?.id ?? emptyOption.id}
                        ref={templatePickerRef} />
                    <Sources salt={salt} />
                    <LanguageField
                        salt={salt}
                        label={formatMessage({
                            id: 'noun_language',
                            defaultMessage: 'Language'
                        })}
                        name="language"
                        field={{
                            value: entity?.language ?? form.language ?? null,
                            required: true
                        }}
                        enabled={!resolving && !entity?.language}
                        whistle={entity?.language ?? form.language ?? 'undecided'}
                        onChange={({ language }) => setForm(form => ({ ...form, language }))} />
                    {(!resolving && !entity?.language) && (
                        <QuickSelectDumb
                            type="language"
                            values={[locale]}
                            currentValues={[form.language]}
                            insertValue={language => setForm(form => ({ ...form, language }))} />
                    )}
                    <ContentLanguageSupportMessage language={form.language} />
                    <CountryField
                        salt={salt}
                        label={formatMessage({
                            id: 'noun_legislation',
                            defaultMessage: 'Legislation'
                        })}
                        name="legislation"
                        field={{ value: entity?.legislation ?? form.legislation ?? null }}
                        enabled={!resolving && !entity?.legislation}
                        whistle={entity?.legislation ?? form.legislation ?? 'undecided'}
                        onChange={({ legislation }) => setForm(form => ({ ...form, legislation }))} />
                    {(!resolving && !entity?.legislation) && (
                        <QuickSelectDumb
                            type="country"
                            values={[account?.address?.country ?? null]}
                            currentValues={[form.legislation]}
                            insertValue={legislation => setForm(form => ({ ...form, legislation }))} />
                    )}
                    {!!initialTemplate?.providedBy && (
                        <Message
                            type="info"
                            message={formatMessage({
                                id: 'documents_smart_templates_use_library_template_disclaimer',
                                defaultMessage: 'When basing your document off this template provided by {company}, they will no longer be responsible for its legality.'
                            }, { company: initialTemplate.providedBy.name })} />
                    )}
                    <Actions className="compact">
                        <Plain
                            onClick={modal.dismiss}
                            className="neutral">
                            <FormattedMessage
                                id="action_cancel"
                                defaultMessage="Cancel" />
                        </Plain>
                        <ButtonSubmit
                            className="constructive"
                            disabled={[
                                !entity,
                                !!size(errors)
                            ].some(Boolean)}
                            ref={trigger}>
                            <FormattedMessage
                                id="action_continue"
                                defaultMessage="Continue" />
                        </ButtonSubmit>
                    </Actions>
                </>
            )}
        </Form>
    )
}

export default AddSmartDocumentContent